import type { Update } from '@ngrx/entity';

import type {
    BatchHistoryChange,
    BatchEntry,
    BatchAddEntry,
    BatchCopyEntry,
    BatchRemoveEntry,
    BatchMoveEntry,
} from './history.model';
import { HistoryChangeAction } from './history.model';

import { Injectable, inject } from '@angular/core';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';

@Injectable({
    providedIn: 'root',
})
export class HistoryServiceV2 {
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly tabsFacade = inject(TabsFacade);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);

    public handleUndo(change: BatchHistoryChange): void {
        for (const entry of change.payload.entries) {
            this.applyEntryUndo(entry);
        }
    }

    public handleRedo(change: BatchHistoryChange): void {
        for (const entry of change.payload.entries) {
            this.applyEntryRedo(entry);
        }
    }

    private applyEntryUndo(entry: BatchEntry): void {
        switch (entry.action) {
            case HistoryChangeAction.update:
                return this.applyUpdate(entry.entityType, entry.before);
            case HistoryChangeAction.add:
                return this.applyAddUndo(entry);
            case HistoryChangeAction.copy:
                return this.applyCopyUndo(entry);
            case HistoryChangeAction.remove:
                return this.applyRemoveUndo(entry);
            case HistoryChangeAction.move:
                return this.applyMoveUndo(entry);
            default:
                console.warn('HistoryServiceV2: Unknown action type', (entry as any).action);
        }
    }

    private applyEntryRedo(entry: BatchEntry): void {
        switch (entry.action) {
            case HistoryChangeAction.update:
                return this.applyUpdate(entry.entityType, entry.after);
            case HistoryChangeAction.add:
                return this.applyAddRedo(entry);
            case HistoryChangeAction.copy:
                return this.applyCopyRedo(entry);
            case HistoryChangeAction.remove:
                return this.applyRemoveRedo(entry);
            case HistoryChangeAction.move:
                return this.applyMoveRedo(entry);
            default:
                console.warn('HistoryServiceV2: Unknown action type', (entry as any).action);
        }
    }

    private applyUpdate(entityType: string, update: Update<any>): void {
        switch (entityType) {
            case 'block':
                return this.blocksFacade.updateBlockProperties(update.id as string, update.changes);
            case 'section':
                return this.sectionsFacade.updateSectionProperties(update.id as string, update.changes);
            case 'column':
                return this.columnsFacade.updateColumnProperties(update.id as string, update.changes);
            case 'grid':
                return this.gridsFacade.updateProperties(update.id as string, update.changes);
            case 'legacyWidget':
                return this.widgetsFacade.updateLegacyWidgetProperties(update.id as string, update.changes);
            case 'widget':
                return this.widgetsFacade.updateWidgetProperties(update.id as string, update.changes);
            case 'tab':
                return this.tabsFacade.updateProperties(update.id as string, update.changes);
            case 'store':
                return this.shopCartIconFacade.saveCartIcon(update.changes as any);
            default:
                console.warn('HistoryServiceV2: Unknown entity type', entityType);
        }
    }

    private applyAddUndo(entry: BatchAddEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.removeBlock(entry.entity, entry.index, true);
            case 'section':
                return this.sectionsFacade.removeSection(entry.entity, entry.index, true);
            case 'column':
                return this.columnsFacade.removeColumn(entry.entity, entry.index, true);
            case 'grid':
                return this.gridsFacade.remove(entry.entity, entry.index, true);
            case 'legacyGridWidget':
                return this.widgetsFacade.removeWidgetFromGrid(entry.entity, entry.index, entry.gridCellIndex, true);
            case 'legacyWidget':
                return this.widgetsFacade.removeWidget(entry.entity, entry.index, true);
            case 'widget':
                return this.removeWidget(entry);
            case 'tab':
                return this.tabsFacade.remove(entry.entity, entry.index, true);
            default:
                console.warn('HistoryServiceV2: Unknown entity type for add undo', entry.entityType);
        }
    }

    private applyAddRedo(entry: BatchAddEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.addBlock(entry.entity, entry.index);
            case 'section':
                return this.sectionsFacade.addSection(entry.entity, entry.index);
            case 'column':
                return this.columnsFacade.addColumn(entry.entity, entry.index);
            case 'grid':
                return this.gridsFacade.add(entry.entity, entry.index);
            case 'legacyGridWidget':
                return this.widgetsFacade.addWidgetToGrid(entry.entity, entry.index, entry.gridCellIndex);
            case 'legacyWidget':
                return this.widgetsFacade.addWidget(entry.entity, entry.index);
            case 'widget':
                return this.addWidget(entry);
            case 'tab':
                this.tabsFacade.add(entry.entity, entry.index);
                entry.entity.children?.forEach((child, index) => {
                    this.gridsFacade.add(child, index);
                });
                return;
            default:
                console.warn('HistoryServiceV2: Unknown entity type for add redo', entry.entityType);
        }
    }

    private applyCopyUndo(entry: BatchCopyEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.removeBlock(entry.entity, entry.index, true);
            case 'column':
                return this.columnsFacade.removeColumn(entry.entity, entry.index, true);
            case 'grid':
                return this.gridsFacade.remove(entry.entity, entry.index, true);
            case 'legacyGridWidget':
                return this.widgetsFacade.removeWidgetFromGrid(entry.entity, entry.index, entry.gridCellIndex, true);
            case 'legacyWidget':
                return this.widgetsFacade.removeWidget(entry.entity, entry.index, true);
            case 'widget':
                return this.removeWidget(entry);
            case 'tab':
                return this.tabsFacade.remove(entry.entity, entry.index, true);
            default:
                console.warn('HistoryServiceV2: Unknown entity type for copy undo', entry.entityType);
        }
    }

    private applyCopyRedo(entry: BatchCopyEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.addBlock(entry.entity, entry.index);
            case 'column':
                return this.columnsFacade.addColumn(entry.entity, entry.index);
            case 'grid':
                return this.gridsFacade.add(entry.entity, entry.index);
            case 'legacyGridWidget':
                return this.widgetsFacade.addWidgetToGrid(entry.entity, entry.index, entry.gridCellIndex);
            case 'legacyWidget':
                return this.widgetsFacade.addWidget(entry.entity, entry.index);
            case 'widget':
                return this.addWidget(entry);
            case 'tab':
                this.tabsFacade.add(entry.entity, entry.index);
                entry.entity.children?.forEach((child, index) => {
                    this.gridsFacade.add(child, index);
                });
                return;
            default:
                console.warn('HistoryServiceV2: Unknown entity type for copy redo', entry.entityType);
        }
    }

    private applyRemoveUndo(entry: BatchRemoveEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.addBlock(entry.entity, entry.index);
            case 'section':
                this.sectionsFacade.addSection({ ...entry.entity, children: [] }, entry.index);
                entry.entity.children?.forEach((column, index) => {
                    this.columnsFacade.addColumn(column, index);
                });
                return;
            case 'column':
                return this.columnsFacade.addColumn(entry.entity, entry.index);
            case 'grid':
                return this.gridsFacade.add(entry.entity, entry.index);
            case 'legacyGridWidget':
                return this.widgetsFacade.addWidgetToGrid(entry.entity, entry.index, entry.gridCellIndex);
            case 'legacyWidget':
                return this.widgetsFacade.addWidget(entry.entity, entry.index);
            case 'widget':
                return this.addWidget(entry);
            case 'tab':
                this.tabsFacade.add(entry.entity, entry.index);
                entry.entity.children?.forEach((child, index) => {
                    this.gridsFacade.add(child, index);
                });
                return;
            default:
                console.warn('HistoryServiceV2: Unknown entity type for remove undo', entry.entityType);
        }
    }

    private applyRemoveRedo(entry: BatchRemoveEntry): void {
        switch (entry.entityType) {
            case 'block':
                return this.blocksFacade.removeBlock(entry.entity, entry.index, true);
            case 'section':
                return this.sectionsFacade.removeSection(entry.entity, entry.index, true);
            case 'column':
                return this.columnsFacade.removeColumn(entry.entity, entry.index, true);
            case 'grid':
                return this.gridsFacade.remove(entry.entity, entry.index, true);
            case 'legacyGridWidget':
                return this.widgetsFacade.removeWidgetFromGrid(entry.entity, entry.index, entry.gridCellIndex, true);
            case 'legacyWidget':
                return this.widgetsFacade.removeWidget(entry.entity, entry.index, true);
            case 'widget':
                return this.removeWidget(entry);
            case 'tab':
                return this.tabsFacade.remove(entry.entity, entry.index, true);
            default:
                console.warn('HistoryServiceV2: Unknown entity type for remove redo', entry.entityType);
        }
    }

    /** A widget entry carries `gridCellIndex` only when the widget sits in a grid cell. */
    private addWidget(entry: BatchAddEntry | BatchCopyEntry | BatchRemoveEntry): void {
        return entry.gridCellIndex === undefined
            ? this.widgetsFacade.addWidget(entry.entity, entry.index)
            : this.widgetsFacade.addWidgetToGrid(entry.entity, entry.index, entry.gridCellIndex);
    }

    private removeWidget(entry: BatchAddEntry | BatchCopyEntry | BatchRemoveEntry): void {
        return entry.gridCellIndex === undefined
            ? this.widgetsFacade.removeWidget(entry.entity, entry.index, true)
            : this.widgetsFacade.removeWidgetFromGrid(entry.entity, entry.index, entry.gridCellIndex, true);
    }

    private applyMoveUndo(entry: BatchMoveEntry): void {
        const p = entry.payload;
        if (p.entityType === 'block') {
            if (p.positionTo < p.positionFrom) {
                return this.blocksFacade.moveDown(p.positionTo, true);
            }
            return this.blocksFacade.moveUp(p.positionTo, true);
        }

        if (p.entityType === 'column') {
            return this.columnsFacade.moveColumnToSection(
                p.entityId,
                p.nextParentId,
                p.prevParentId,
                p.positionTo,
                p.positionFrom,
                true
            );
        }

        if (p.entityType === 'legacyGridWidget') {
            if (p.type === 'fromGridToColumn') {
                return this.gridsFacade.moveWidgetFromColumnToGrid(
                    p.entityId,
                    p.fromCell,
                    p.nextParentId,
                    p.prevParentId,
                    p.positionTo,
                    p.positionFrom,
                    true
                );
            }
            if (p.type === 'fromColumnToGrid') {
                return this.gridsFacade.moveWidgetFromGridToColumn(
                    p.entityId,
                    p.toCell,
                    p.nextParentId,
                    p.prevParentId,
                    p.positionTo,
                    p.positionFrom,
                    true
                );
            }
            if (p.type === 'fromGridToGrid') {
                return this.gridsFacade.moveWidgetFromGridToGrid(
                    p.entityId,
                    p.toCell,
                    p.fromCell,
                    p.nextParentId,
                    p.prevParentId,
                    p.positionTo,
                    p.positionFrom,
                    true
                );
            }
        }

        if (p.entityType === 'legacyWidget') {
            return this.widgetsFacade.moveWidgetToColumn(
                p.entityId,
                p.nextParentId,
                p.prevParentId,
                p.positionTo,
                p.positionFrom,
                true
            );
        }

        console.warn('HistoryServiceV2: Unknown entity type for move undo', p.entityType);
    }

    private applyMoveRedo(entry: BatchMoveEntry): void {
        const p = entry.payload;
        if (p.entityType === 'block') {
            if (p.positionTo < p.positionFrom) {
                return this.blocksFacade.moveDown(p.positionTo, true);
            }
            return this.blocksFacade.moveUp(p.positionTo, true);
        }

        if (p.entityType === 'column') {
            return this.columnsFacade.moveColumnToSection(
                p.entityId,
                p.prevParentId,
                p.nextParentId,
                p.positionFrom,
                p.positionTo,
                true
            );
        }

        if (p.entityType === 'legacyGridWidget') {
            if (p.type === 'fromGridToColumn') {
                return this.gridsFacade.moveWidgetFromGridToColumn(
                    p.entityId,
                    p.fromCell,
                    p.prevParentId,
                    p.nextParentId,
                    p.positionFrom,
                    p.positionTo,
                    true
                );
            }
            if (p.type === 'fromColumnToGrid') {
                return this.gridsFacade.moveWidgetFromColumnToGrid(
                    p.entityId,
                    p.toCell,
                    p.prevParentId,
                    p.nextParentId,
                    p.positionFrom,
                    p.positionTo,
                    true
                );
            }
            if (p.type === 'fromGridToGrid') {
                return this.gridsFacade.moveWidgetFromGridToGrid(
                    p.entityId,
                    p.fromCell,
                    p.toCell,
                    p.prevParentId,
                    p.nextParentId,
                    p.positionFrom,
                    p.positionTo,
                    true
                );
            }
        }

        if (p.entityType === 'legacyWidget') {
            return this.widgetsFacade.moveWidgetToColumn(
                p.entityId,
                p.prevParentId,
                p.nextParentId,
                p.positionFrom,
                p.positionTo,
                true
            );
        }

        console.warn('HistoryServiceV2: Unknown entity type for move redo', p.entityType);
    }
}
